import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconClose, IconInfo } from './icons';
import { Button } from './ui';

/**
 * A small "about this software" disclosure, tucked above the Micro Tech
 * credit line in the sidebar footer, a place someone looks for exactly this,
 * and nowhere the page has to make room for it.
 *
 * The content below is a plain-language summary grounded in what this
 * application actually does (SPEC.md §7/§8), not a drafted legal document.
 * Treat it as a starting point for whatever Micro Tech Namibia's real terms
 * end up being, not a substitute for them.
 */
export function LegalNoticeButton() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="About this software, terms and privacy"
        title="About this software"
        className="mb-2 flex items-center gap-1 rounded-md px-0.5 text-slate-400 transition-colors hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
      >
        <IconInfo className="h-3.5 w-3.5 shrink-0" />
        <span>About</span>
      </button>
      {open && <LegalNoticeDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function LegalNoticeDialog({ onClose }: { onClose: () => void }) {
  const panel = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // preventScroll: with five sections this panel now scrolls internally,
    // and a plain .focus() on the Close button (down at the bottom) pulls
    // the browser's focus-follows-scroll behaviour down with it, opening the
    // dialog already scrolled past its own title.
    closeRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Portalled to the document body: rendered from inside the sidebar drawer,
  // which carries a `transform` for its open/close slide. A `transform` on
  // any ancestor makes it the containing block for a `position: fixed`
  // descendant, so without the portal this dialog would be trapped inside
  // the sidebar's own width and height instead of covering the page.
  return createPortal(
    <div
      className="animate-fade fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="About this software"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        className="animate-pop max-h-[85vh] w-full max-w-md overflow-y-auto rounded-xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-800 dark:bg-slate-900"
      >
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-base font-semibold tracking-tight text-slate-900 dark:text-white">
            About this software
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mt-1 -mr-1 rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            <IconClose className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 space-y-5 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
          <section>
            <h3 className="mb-1 text-xs font-semibold tracking-wider text-slate-500 uppercase dark:text-slate-400">
              What this is
            </h3>
            <p>
              A digital replacement for New Horizon Copper's paper "New User IT Enrolment Form". The same
              questions and the same three signatures, but it saves as you go, reminds people when something is
              waiting on them, and the finished record is saved afterwards.
            </p>
          </section>

          <section>
            <h3 className="mb-1 text-xs font-semibold tracking-wider text-slate-500 uppercase dark:text-slate-400">
              Terms of use
            </h3>
            <p>
              For New Horizon Copper staff, to raise and process genuine IT enrolment and offboarding requests.
              Sign-in is tied to your NHC identity, and every signature you make is attributed to you and cannot
              be altered once given.
            </p>
          </section>

          <section>
            <h3 className="mb-1 text-xs font-semibold tracking-wider text-slate-500 uppercase dark:text-slate-400">
              Privacy
            </h3>
            <p>
              ID/passport and contact numbers are encrypted before they are stored, and shown only to someone
              with a genuine reason to see them: the person the request concerns, whoever approves it, and IT
              completing it. Substantive changes are written to an append-only audit trail. Signed records are
              kept as permanent proof of what was requested and granted; how long they are kept is a retention
              policy NHC has set, not something enforced automatically.
            </p>
          </section>

          <section>
            <h3 className="mb-1 text-xs font-semibold tracking-wider text-slate-500 uppercase dark:text-slate-400">
              Ownership
            </h3>
            <p>
              This software is built and operated by Micro Tech Namibia for New Horizon Copper, and belongs to
              Micro Tech Namibia at this time. Crafted by Fred.
            </p>
          </section>

          <section>
            <h3 className="mb-1 text-xs font-semibold tracking-wider text-slate-500 uppercase dark:text-slate-400">
              Support
            </h3>
            <p>
              For help, a problem with a request, or to report something wrong, reach out to us via{' '}
              <a
                href="mailto:support@microtechnamibia.com"
                className="font-medium text-brand-700 underline-offset-4 hover:underline dark:text-brand-300"
              >
                email
              </a>{' '}
              or the IT WhatsApp group.
            </p>
          </section>

          <p className="border-t border-slate-100 pt-4 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-500">
            A summary, not the full terms. Micro Tech Namibia © {new Date().getFullYear()}.
          </p>
        </div>

        <div className="mt-6 flex justify-end">
          <Button ref={closeRef} variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
